// src/components/SignupForm.js
import React, { Component } from 'react';
import Icon from './Icon';
import { postRequest, API_ENDPOINTS } from '../config/apiConfig';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

/* ── Password strength scorer ───────────────────────────────── */
function scorePassword(pw) {
  if (!pw) return { score: 0, label: '', cls: '' };
  let s = 0;
  if (pw.length >= 8)           s++;
  if (pw.length >= 12)          s++;
  if (/[A-Z]/.test(pw))        s++;
  if (/[0-9]/.test(pw))        s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { score: 1, label: 'Weak',   cls: 'weak'   };
  if (s === 2) return { score: 2, label: 'Fair',   cls: 'fair'   };
  if (s === 3) return { score: 3, label: 'Good',   cls: 'good'   };
  return              { score: 4, label: 'Strong', cls: 'strong' };
}

class SignupForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      firstName:   '',
      lastName:    '',
      email:       '',
      mobile:      '',
      dob:         '',
      gender:      '',
      blood:       '',
      address:     '',
      password:    '',
      confirmPwd:  '',
      showPass:    false,
      showConfirm: false,
      agreeTerms:  false,
      loading:     false,
      errors:      {},
      submitted:   false,
    };
  }

  /* ── Field updater ── */
  setField(key, val) {
    this.setState(s => ({
      [key]: val,
      errors: { ...s.errors, [key]: '' },
    }));
  }

  /* ── Validation ── */
  validate() {
    const {
      firstName, lastName, email, mobile,
      password, confirmPwd, gender, agreeTerms,
    } = this.state;
    const errs = {};

    if (!firstName.trim())
      errs.firstName = 'First name is required.';

    if (!lastName.trim())
      errs.lastName = 'Last name is required.';

    if (!email.trim())
      errs.email = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = 'Enter a valid email address.';

    if (!mobile.trim())
      errs.mobile = 'Mobile number is required.';
    else if (!/^\d{10}$/.test(mobile.replace(/\s/g, '')))
      errs.mobile = 'Enter a valid 10-digit mobile number.';

    if (!gender)
      errs.gender = 'Please select your gender.';

    if (!password)
      errs.password = 'Password is required.';
    else if (password.length < 8)
      errs.password = 'Password must be at least 8 characters.';

    if (!confirmPwd)
      errs.confirmPwd = 'Please confirm your password.';
    else if (password !== confirmPwd)
      errs.confirmPwd = 'Passwords do not match.';

    if (!agreeTerms)
      errs.agreeTerms = 'You must accept the Terms & Privacy Policy.';

    return errs;
  }

  /* ── Submit ── */
  async handleSubmit(e) {
    e.preventDefault();

    // Step 1 — Client-side validation
    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    // Step 2 — Build JSON payload from state
    const {
      firstName, lastName, email, mobile,
      dob, gender, blood, address, password,
    } = this.state;

    const payload = {
      first_name:   firstName.trim(),
      last_name:    lastName.trim(),
      email:        email.trim().toLowerCase(),
      mobile:       mobile.trim(),
      dob:          dob         || null,
      gender:       gender      || null,
      blood_group:  blood       || null,
      address:      address.trim() || null,
      password:     password,
      user_type:"patient"
    };

    // Step 3 — Call the API using postRequest from apiConfig
    this.setState({ loading: true, errors: {} });

    try {
      const response = await postRequest(API_ENDPOINTS.REGISTER, payload);

      const data = await response.json();

      // Step 4 — Handle response
      if (response.ok) {
        // Success — account created
        this.setState({
          loading:   false,
          submitted: true,
          formData: {
            firstName: data.first_name || firstName,
            lastName:  data.last_name  || lastName,
            email:     data.email      || email,
          },
        });



        setTimeout(() => {
          if (this.props.onSwitchToLogin) this.props.onSwitchToLogin();
        }, 3000);
      } else {
        // Server returned an error (400, 409, 422 etc.)
        const serverError =
          data.message ||
          data.error   ||
          (data.errors && Object.values(data.errors)[0]) ||
          'Registration failed. Please try again.';

        this.setState({
          loading: false,
          errors:  { submit: serverError },
        });
      }

    } catch (err) {
      // Network error or server unreachable
      this.setState({
        loading: false,
        errors:  {
          submit: 'Unable to connect to the server. Please check your internet connection.',
        },
      });
    }
  }

  /* ── Reusable text/email/tel field ── */
  renderField(key, label, type = 'text', placeholder = '', iconName = 'user', required = true) {
    const { errors, loading } = this.state;
    const val = this.state[key];
    return (
      <div className="auth-field">
        <label className="auth-field-label">
          {label}
          {required && <span className="auth-required"> *</span>}
        </label>
        <div className="auth-input-wrap">
          <span className="auth-input-icon">
            <Icon name={iconName} size={16} />
          </span>
          <input
            type={type}
            className={`auth-input${errors[key] ? ' error' : ''}`}
            placeholder={placeholder}
            value={val}
            onChange={e => this.setField(key, e.target.value)}
            disabled={loading}
            autoComplete={type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'off'}
          />
        </div>
        {errors[key] && (
          <div className="auth-field-error">
            <Icon name="alert" size={12} color="var(--error)" />
            {errors[key]}
          </div>
        )}
      </div>
    );
  }

  /* ── Success screen ── */
  renderSuccess() {
    const { firstName, lastName, email } = this.state;
    return (
      <div className="auth-success-screen">
        <div className="auth-success-icon">
          <Icon name="check" size={34} color="var(--success)" />
        </div>
        <div className="auth-success-title">Account Created! 🎉</div>
        <p className="auth-success-sub">
          Your DG Doctor account has been registered successfully.
          An admin will assign your role. You can now sign in.
        </p>
        <div className="auth-success-detail">
          {[
            { k: 'Name',   v: `${firstName} ${lastName}` },
            { k: 'Email',  v: email                      },
            { k: 'Role',   v: 'Assigned by Admin'         },
            { k: 'Status', v: 'Pending Activation'        },
          ].map((row, i) => (
            <div key={i} className="auth-success-row">
              <span className="auth-success-key">{row.k}</span>
              <span className="auth-success-val">{row.v}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="auth-btn"
          onClick={() => { if (this.props.onSwitchToLogin) this.props.onSwitchToLogin(); }}
        >
          Go to Sign In
          <Icon name="chevronR" size={18} color="#fff" />
          <span className="auth-btn-shimmer" />
        </button>
      </div>
    );
  }

  /* ── Main render ── */
  render() {
    const {
      gender, blood, address,
      password, confirmPwd,
      showPass, showConfirm,
      agreeTerms, loading, errors, submitted, dob,
    } = this.state;

    if (submitted) return this.renderSuccess();

    const pwdStrength = scorePassword(password);

    return (
      <div style={{ animation: 'authFadeUp 0.4s ease both' }}>

        {/* ── Header ── */}
        <div className="auth-form-header">
          <h2 className="auth-form-title">Create your account ✨</h2>
          <p className="auth-form-subtitle">
            Fill in your details below. Your role will be assigned by the hospital admin.
          </p>
        </div>

        <form onSubmit={e => this.handleSubmit(e)} noValidate>

          {/* Server / submit error */}
          {errors.submit && (
            <div className="auth-alert error">
              <Icon name="alert" size={15} color="var(--error)" />
              {errors.submit}
            </div>
          )}

          {/* ══ Personal Information ══ */}
          <div className="auth-section-title">Personal Information</div>

          {/* First & Last name row */}
          <div className="auth-row">
            {this.renderField('firstName', 'First Name', 'text', 'First name', 'user')}
            {this.renderField('lastName',  'Last Name',  'text', 'Last name',  'user')}
          </div>

          {/* Email */}
          {this.renderField('email', 'Email Address', 'email', 'you@example.com', 'mail')}

          {/* Mobile */}
          {this.renderField('mobile', 'Mobile Number', 'tel', '10-digit number', 'phone')}

          {/* DOB + Gender row */}
          <div className="auth-row">

            {/* Date of birth */}
            <div className="auth-field">
              <label className="auth-field-label">Date of Birth</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon">
                  <Icon name="clock" size={16} />
                </span>
                <input
                  type="date"
                  className="auth-input"
                  value={dob}
                  onChange={e => this.setField('dob', e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Gender */}
            <div className="auth-field">
              <label className="auth-field-label">
                Gender <span className="auth-required">*</span>
              </label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon">
                  <Icon name="users" size={16} />
                </span>
                <select
                  className={`auth-select${errors.gender ? ' error' : ''}`}
                  value={gender}
                  onChange={e => this.setField('gender', e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                  <option>Prefer not to say</option>
                </select>
              </div>
              {errors.gender && (
                <div className="auth-field-error">
                  <Icon name="alert" size={12} color="var(--error)" />
                  {errors.gender}
                </div>
              )}
            </div>

          </div>

          {/* Blood Group */}
          <div className="auth-field">
            <label className="auth-field-label">Blood Group</label>
            <div className="auth-input-wrap">
              <span className="auth-input-icon">
                <Icon name="info" size={16} />
              </span>
              <select
                className="auth-select"
                value={blood}
                onChange={e => this.setField('blood', e.target.value)}
                disabled={loading}
              >
                <option value="">Select blood group (optional)</option>
                {BLOOD_GROUPS.map(bg => (
                  <option key={bg}>{bg}</option>
                ))}
              </select>
            </div>
          </div>

     

          {/* ══ Set Password ══ */}
          <div className="auth-section-title">Set Password</div>

          {/* Password */}
          <div className="auth-field">
            <label className="auth-field-label">
              Password <span className="auth-required">*</span>
            </label>
            <div className="auth-input-wrap">
              <span className="auth-input-icon">
                <Icon name="lock" size={16} />
              </span>
              <input
                type={showPass ? 'text' : 'password'}
                className={`auth-input has-action${errors.password ? ' error' : ''}`}
                placeholder="Minimum 8 characters"
                value={password}
                onChange={e => this.setField('password', e.target.value)}
                disabled={loading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => this.setState(s => ({ showPass: !s.showPass }))}
              >
                <Icon name={showPass ? 'eyeOff' : 'eye'} size={14} />
                {showPass ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <div className="auth-field-error">
                <Icon name="alert" size={12} color="var(--error)" />
                {errors.password}
              </div>
            )}
            {/* Strength meter */}
            {password && (
              <div className="auth-pwd-strength">
                <div className="auth-pwd-bars">
                  {[1, 2, 3, 4].map(n => (
                    <div
                      key={n}
                      className={`auth-pwd-bar${pwdStrength.score >= n ? ` fill-${pwdStrength.cls}` : ''}`}
                    />
                  ))}
                </div>
                <span className={`auth-pwd-label ${pwdStrength.cls}`}>
                  {pwdStrength.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div className="auth-field">
            <label className="auth-field-label">
              Confirm Password <span className="auth-required">*</span>
            </label>
            <div className="auth-input-wrap">
              <span className="auth-input-icon">
                <Icon name="lock" size={16} />
              </span>
              <input
                type={showConfirm ? 'text' : 'password'}
                className={`auth-input has-action${errors.confirmPwd ? ' error' : ''}`}
                placeholder="Re-enter your password"
                value={confirmPwd}
                onChange={e => this.setField('confirmPwd', e.target.value)}
                disabled={loading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => this.setState(s => ({ showConfirm: !s.showConfirm }))}
              >
                <Icon name={showConfirm ? 'eyeOff' : 'eye'} size={14} />
                {showConfirm ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.confirmPwd && (
              <div className="auth-field-error">
                <Icon name="alert" size={12} color="var(--error)" />
                {errors.confirmPwd}
              </div>
            )}
            {/* Match indicator */}
            {confirmPwd && password && confirmPwd === password && !errors.confirmPwd && (
              <div className="auth-field-error auth-field-success">
                <Icon name="check" size={12} color="var(--success)" />
                Passwords match
              </div>
            )}
          </div>

          {/* ══ Terms ══ */}
          <div
            className="auth-check-row"
            onClick={() => this.setField('agreeTerms', !agreeTerms)}
          >
            <div className={`auth-checkbox${agreeTerms ? ' checked' : ''}`}>
              {agreeTerms && <Icon name="check" size={11} color="#fff" />}
            </div>
            <span className="auth-check-text">
              I agree to the{' '}
              <a href="#terms" onClick={e => e.stopPropagation()}>Terms of Service</a>
              {' '}and{' '}
              <a href="#privacy" onClick={e => e.stopPropagation()}>Privacy Policy</a>
              {' '}of DG Doctor Hospital Management System.
            </span>
          </div>
          {errors.agreeTerms && (
            <div className="auth-field-error auth-terms-error">
              <Icon name="alert" size={12} color="var(--error)" />
              {errors.agreeTerms}
            </div>
          )}


          {/* ══ Submit ══ */}
          <button
            type="submit"
            className="auth-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <Icon name="spinner" size={18} color="#fff" className="auth-btn-spinner" />
                Creating account…
              </>
            ) : (
              <>
                Create Account
                <Icon name="chevronR" size={18} color="#fff" />
                <span className="auth-btn-shimmer" />
              </>
            )}
          </button>

        </form>


      </div>
    );
  }
}

export default SignupForm;
